import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AppConfigSettings, AppSettings } from '../../models';
import { IonicCustomizing } from '../../types';
import { LogService } from '../log/log.service';
import { ConfigProvider } from './config-provider';

@Injectable()
export class ConfigAppConfigProvider implements ConfigProvider {
  private readonly http: HttpClient = inject(HttpClient);
  public readonly loadOrder = 2;

  public async getAppSettings(appSettings: AppSettings): Promise<AppSettings> {
    const fromConfig = await this.loadFromFile('./app-config.json');
    if (fromConfig === null) {
      LogService.error('Could not load internal config file');
      return appSettings;
    }

    if (!ConfigAppConfigProvider.isValidUrl(fromConfig.authServerUrl)) {
      LogService.error(`app-config.json: invalid authServerUrl "${fromConfig.authServerUrl}" — configuration rejected`);
      return appSettings;
    }
    if (!ConfigAppConfigProvider.isValidUrl(fromConfig.resourceServerUrl)) {
      LogService.error(
        `app-config.json: invalid resourceServerUrl "${fromConfig.resourceServerUrl}" — configuration rejected`,
      );
      return appSettings;
    }

    if (fromConfig.redirectUri !== undefined && !ConfigAppConfigProvider.isValidRedirectUri(fromConfig.redirectUri)) {
      LogService.error(`app-config.json: invalid redirectUri "${fromConfig.redirectUri}" — configuration rejected`);
      return appSettings;
    }
    if (
      fromConfig.postLogoutRedirectUri !== undefined &&
      !ConfigAppConfigProvider.isValidRedirectUri(fromConfig.postLogoutRedirectUri)
    ) {
      LogService.error(
        `app-config.json: invalid postLogoutRedirectUri "${fromConfig.postLogoutRedirectUri}" — configuration rejected`,
      );
      return appSettings;
    }

    appSettings.connection = {
      ...appSettings.connection,
      auth: fromConfig.authServerUrl,
      res: fromConfig.resourceServerUrl,
      clientId: fromConfig.clientId,
      clientSecret: fromConfig.clientSecret,
      redirectUri: fromConfig.redirectUri,
      postLogoutRedirectUri: fromConfig.postLogoutRedirectUri,
      autoClosePostLogoutPage: fromConfig.autoClosePostLogoutPage ?? false,
    };
    appSettings.dashboard = {
      ...appSettings.dashboard,
      menuTiles: fromConfig.menuTiles ?? [],
    };
    appSettings.tutorial = fromConfig.tutorial ?? appSettings.tutorial;
    appSettings.showDataPrivacyPage = fromConfig.showDataPrivacyPage ?? false;

    // Customizing: set menu/footerNavigation from top-level config, then apply section-level flags
    appSettings.customizing = this.parseCustomizing(fromConfig.customizing, {
      ...appSettings.customizing,
      menu: fromConfig.menu,
      footerNavigation: {
        INIT: fromConfig.footerNavigation?.INIT ?? [],
        SIGNED_IN: fromConfig.footerNavigation?.SIGNED_IN ?? [],
      },
    });

    // Mobile-only Ionic customizations (scanner, status bar, footer, dashboard header)
    if (fromConfig.customizing?.ionic) {
      this.applyIonic(appSettings, fromConfig.customizing.ionic);
    }

    // Request (merge with existing)
    appSettings.request = this.parseRequest(fromConfig.request, appSettings.request);

    return appSettings;
  }

  public parseCustomizing(
    configCustomizing: AppConfigSettings['customizing'] | undefined,
    existingCustomizing: AppSettings['customizing'],
  ): AppSettings['customizing'] {
    if (!configCustomizing) {
      return existingCustomizing;
    }

    return {
      ...existingCustomizing,
      allowPdfUpload: configCustomizing.allowPdfUpload ?? false,
      allowUserSelfDelete: configCustomizing.allowUserSelfDelete ?? false,
      enableInAppRating: configCustomizing.enableInAppRating ?? false,
      hasBescheide: configCustomizing.hasBescheide ?? false,
      hasUebersicht: configCustomizing.hasUebersicht ?? false,
      hasUnfinishedAntrage: configCustomizing.hasUnfinishedAntrage ?? false,
      preventUnlockingWithLogin: configCustomizing.preventUnlockingWithLogin ?? false,
    };
  }

  /**
   * Applies the mobile-only `customizing.ionic` block onto the relevant AppSettings branches:
   * scanner/status-bar/footer land under `customizing`, the dashboard content-header under `dashboard`.
   * Centralizes all Ionic mapping in one place so the two target branches stay in sync.
   */
  public applyIonic(appSettings: AppSettings, ionic: IonicCustomizing): void {
    if (ionic.scanbot) {
      appSettings.customizing.scanbot = ionic.scanbot;
    }
    if (ionic.appContent) {
      appSettings.customizing.appContent = ionic.appContent;
    }
    if (ionic.footer) {
      appSettings.customizing.ui = {
        ...appSettings.customizing.ui,
        app: {
          ...appSettings.customizing.ui?.app,
          footer: {
            hasCustomBorder: ionic.footer.hasCustomBorder ?? false,
            hasCustomBackground: ionic.footer.hasCustomBackground ?? false,
          },
        },
      };
    }
    if (ionic.dashboard?.contentHeader) {
      appSettings.dashboard = {
        ...appSettings.dashboard,
        contentHeader: ionic.dashboard.contentHeader,
      };
    }
  }

  public parseRequest(
    configRequest: AppConfigSettings['request'] | undefined,
    existingRequest: AppSettings['request'],
  ): AppSettings['request'] {
    if (!configRequest) {
      return existingRequest;
    }

    return {
      ...existingRequest,
      hasCustomRequestStates: configRequest.hasCustomRequestStates ?? false,
      hasCustomRequestType: configRequest.hasCustomRequestType ?? false,
    };
  }

  private loadFromFile(url: string): Promise<AppConfigSettings | null> {
    return firstValueFrom(this.http.get<AppConfigSettings>(url)).catch(() => null);
  }

  private static isValidUrl(value: string | undefined): boolean {
    if (!value) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || (url.protocol === 'http:' && url.hostname === 'localhost');
    } catch {
      return false;
    }
  }

  // Redirect URIs allow the app's custom scheme in addition to http/https
  private static isValidRedirectUri(value: string | undefined): boolean {
    if (!value) return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'ebeihilfe:';
    } catch {
      return false;
    }
  }
}
